import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getProfile, updateProfile } from "@/api/profile";
import { getCV, updateCV, ingestCV } from "@/api/cv";
import { listApplications, updateStatus, deleteApplication } from "@/api/applications";
import {
  getCandidateGraph,
  putCandidateGraph,
  patchCandidateGraphMeta,
  assembleCandidateMarkdown,
  parseCandidateMarkdown,
  commitCandidateDraft,
  getAtsPastePack,
} from "@/api/candidate-graph";
import type {
  Profile,
  CandidateGraphPutBody,
  CandidateGraphDraft,
  NarrativeLensId,
} from "@career-ops-amplify/shared";
import type { ProfilePatch, ApplicationQuery } from "@/types";

// ---------------------------------------------------------------------------
// Profile hooks
// ---------------------------------------------------------------------------

const PROFILE_KEY = ["profile"] as const;

export function useProfile() {
  return useQuery<Profile>({
    queryKey: PROFILE_KEY,
    queryFn: getProfile,
    staleTime: 60_000,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProfilePatch) => updateProfile(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_KEY }),
  });
}

// ---------------------------------------------------------------------------
// CV hooks
// ---------------------------------------------------------------------------

const CV_KEY = ["cv"] as const;

export function useCV() {
  return useQuery({
    queryKey: CV_KEY,
    queryFn: getCV,
    staleTime: 60_000,
  });
}

export function useUpdateCV() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) => updateCV(content),
    onSuccess: () => qc.invalidateQueries({ queryKey: CV_KEY }),
  });
}

export function useIngestCV() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => ingestCV(text),
    onSuccess: () => qc.invalidateQueries({ queryKey: CV_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Application hooks
// ---------------------------------------------------------------------------

const APPS_KEY = ["applications"] as const;

export function useApplications(params?: ApplicationQuery) {
  return useQuery({
    // Include serialised params in query key so refetch picks up changes
    queryKey: [...APPS_KEY, params],
    queryFn: () => listApplications(params),
    staleTime: 30_000,
  });
}

export function useUpdateStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      n,
      status,
      notes,
    }: {
      n: number;
      status: string;
      notes?: string;
    }) => updateStatus(n, status, notes),
    onSuccess: () => {
      // Invalidate both the listing and the pipeline (which also carries apps)
      qc.invalidateQueries({ queryKey: APPS_KEY });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });
}

export function useDeleteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (n: number) => deleteApplication(n),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: APPS_KEY });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Candidate Intelligence Graph hooks
// ---------------------------------------------------------------------------

const CIG_KEY = ["candidate-graph"] as const;

export function useCandidateGraph() {
  return useQuery({
    queryKey: CIG_KEY,
    queryFn: getCandidateGraph,
    staleTime: 30_000,
  });
}

export function useInvalidateCandidateGraph() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: CIG_KEY });
    void qc.invalidateQueries({ queryKey: CV_KEY });
  };
}

export function usePutCandidateGraph() {
  const invalidate = useInvalidateCandidateGraph();
  return useMutation({
    mutationFn: (body: CandidateGraphPutBody) => putCandidateGraph(body),
    onSuccess: () => invalidate(),
  });
}

export function usePatchCandidateGraphMeta() {
  const invalidate = useInvalidateCandidateGraph();
  return useMutation({
    mutationFn: patchCandidateGraphMeta,
    onSuccess: () => invalidate(),
  });
}

export function useAssembleCandidateMarkdown() {
  const invalidate = useInvalidateCandidateGraph();
  return useMutation({
    mutationFn: assembleCandidateMarkdown,
    onSuccess: () => invalidate(),
  });
}

export function useParseCandidateMarkdown() {
  return useMutation({
    mutationFn: (markdown?: string) => parseCandidateMarkdown(markdown),
  });
}

export function useCommitCandidateDraft() {
  const invalidate = useInvalidateCandidateGraph();
  return useMutation({
    mutationFn: (draft: CandidateGraphDraft) => commitCandidateDraft(draft),
    onSuccess: () => invalidate(),
  });
}

export function useAtsPastePack(lens?: NarrativeLensId) {
  return useQuery({
    queryKey: [...CIG_KEY, "paste-pack", lens ?? "default"],
    queryFn: () => getAtsPastePack(lens),
    enabled: false,
  });
}